import React, { useState } from "react";
import { Select } from "antd";
import Alert from "../components/Alert";
import StepFooter from "./StepFooter";
import { useIsMobile } from "Hooks";
import { Icon } from "Components";
import OrganizationRecipients from "./Step3SelectionMethods/OrganizationRecipients";
import RoleRecipients from "./Step3SelectionMethods/RoleRecipients";

interface StepProps {
  onBack: () => void;
  onNext: () => void;
}

type SelectionMethod =
  | "organization"
  | "property"
  | "role"
  | "group"
  | "individual";

interface SelectorOption {
  key: SelectionMethod;
  icon: React.ReactNode;
  label: string;
  description: string;
}

const selectionOptions: SelectorOption[] = [
  {
    key: "organization",
    icon: <Icon type="recipientBuilding" />,
    label: "By Organization",
    description: "Send to all users in your organization",
  },
  {
    key: "property",
    icon: <Icon type="recipientProperty" />,
    label: "By Property",
    description: "Select entire properties",
  },
  {
    key: "role",
    icon: <Icon type="recipientRole" />,
    label: "By Role",
    description: "Send to all users assigned to a specific role",
  },
  {
    key: "group",
    icon: <Icon type="recipientGroup" />,
    label: "By Group",
    description: "Select groups or departments",
  },
  {
    key: "individual",
    icon: <Icon type="recipientIndividual" />,
    label: "By Individual",
    description: "Select specific individuals by name or email",
  },
];



const roleOptions = [
  {
    key: "staff",
    label: "Staff",
    count: 40,
    description: "All users other than Managers and Leadership team",
  },
  {
    key: "manager",
    label: "Manager",
    count: 5,
    description: "Department and property managers",
  },
  {
    key: "leadership",
    label: "Leadership and IT",
    count: 2,
    description: "Those overseeing system and strategy",
  },
];

const propertyOptions = [
  { value: "property1", label: "Property 1" },
  { value: "property2", label: "Property 2" },
  { value: "property3", label: "Property 3" },
];

const propertyRoles: Record<string, string[]> = {
  property1: ["staff", "manager", "leadership"],
  property2: ["staff", "manager"],
  property3: ["staff"],
};

const Step3Recipients: React.FC<StepProps> = ({ onBack, onNext }) => {
  const [selectedMethod, setSelectedMethod] = useState<SelectionMethod>("organization");
  const [organizationSelected, setOrganizationSelected] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string | undefined>(undefined);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const isMobile = useIsMobile();


  return (
    <>
      <div className="step-card phishing-step3">
        <div className="card-header">
          <div className="card-title">Full Campaign Recipients</div>
          <div className="card-subtitle">
            Select the recipients who will receive the full phishing campaign
            once the test is completed.
          </div>
        </div>

        <div className="phishing-step3__selection">
          <div className="phishing-step3__label">
            Selection method <span className="required">*</span>
          </div>
          {isMobile ? (
            <div className="phishing-step3__dropdown--mobile">
              <Select
                style={{ width: "100%" }}
                value={selectedMethod}
                onChange={(value) => setSelectedMethod(value as SelectionMethod)}
                options={selectionOptions.map((opt) => ({
                  value: opt.key,
                  label: opt.label,
                }))}
                size="large"
              />
            </div>
          ) : (
            <div className="phishing-step3__grid">
              {selectionOptions.map((opt) => {
                const active = selectedMethod === opt.key;
                return (
                  <div
                    key={opt.key}
                    onClick={() => setSelectedMethod(opt.key)}
                    className={`phishing-step3__selector ${
                      active ? "phishing-step3__selector--active" : ""
                    }`}
                  >
                    <div className="phishing-step3__selector-header">
                      <div className="phishing-step3__icon">{opt.icon}</div>
                      <span className="phishing-step3__selector-title">
                        {opt.label}
                      </span>
                    </div>
                    <span className="phishing-step3__selector-desc">
                      {opt.description}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedMethod === "organization" && (
          <OrganizationRecipients
            organizationSelected={organizationSelected}
            setOrganizationSelected={setOrganizationSelected}
          />
        )}

        {selectedMethod === "role" && (
          <RoleRecipients
            propertyOptions={propertyOptions}
            roleOptions={roleOptions}
            propertyRoles={propertyRoles}
            selectedRoles={selectedRoles}
            setSelectedRoles={setSelectedRoles}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            roleSearchQuery={roleSearchQuery}
            setRoleSearchQuery={setRoleSearchQuery}
          />
        )}

        <div className="alert-section">
          <Alert subtitle="No emails will be sent to these recipients until the test is completed and the full campaign is approved" />
        </div>
      </div>
      <StepFooter
        onBack={onBack}
        onNext={onNext}
        nextBtnDisabled={
          selectedMethod === "organization"
            ? !organizationSelected
            : selectedMethod === "role"
            ? selectedRoles.length === 0
            : false
        }
      />
    </>
  );
};

export default Step3Recipients;